import { Injectable, inject } from '@angular/core';
import { AgentCategory } from '../models/agent-config.model';
import { AiClientService } from '../services/ai-client.service';
import { AiConversation, AiConversationItem, AiConversationSummary, AiRequestOptions, TokenUsage } from '../transports/ai-transport';
import { unfenceJson } from '../transports/json-fence';
import { DiskService } from '@omni/services/disk/disk.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { AuthenticationService } from '@omni/services/authentication.service';
import { AssistantAttachment, ConversationOrigin, buildAttachmentContext } from './assistant-attachment';
import { CardAction, CardResponse, parseCardResponse } from './card.schema';
import { NovaConversationIndexService, NovaConversationEntry } from '../services/nova-conversation-index.service';
import { markdownToText } from './markdown-text';
import { GeolocationService } from '@omni/services/location/gelolocation';
import { firstValueFrom, of } from 'rxjs';
import { LocalizationService } from '@omni/services/localization/localization.service';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';

export interface AssistantChatMessage {
  role: string; // 'user' | 'assistant'
  text: string;
  /**
   * The assistant turn's unified card (text / callout / charts / entities /
   * follow-up action chips). Null for user turns and for a turn the agent
   * answered with no structured envelope. Rendered by `<nova-card>`.
   */
  card?: CardResponse | null;
  /**
   * Attachments the rep sent with this user turn. Rendered as small pills
   * above/inside the user bubble.
   *
   * LIVE PATH: the parent (nova-voice) must set this field on the optimistic
   * user message it pushes before calling feature.send(). This component owns
   * only the rendering; the parent owns the push. See PARENT CONTRACT note.
   *
   * RESTORE PATH: #toMessages parses attachment labels from the developer
   * context block that is persisted server-side with the conversation. This
   * gives best-effort display for history (labels only, no ids needed to render).
   */
  attachments?: AssistantAttachment[] | null;
  /** Responses API id of the turn that produced this message, for feedback correlation.
   *  Undefined for user turns and for assistant turns restored from history (no live send). */
  responseId?: string;
  /** Token usage for the turn that produced this message. Same restore-path caveat as above. */
  usage?: TokenUsage;
}

/** Typed assistant reply — the unified card plus a plain-text digest. */
export interface AssistantReply {
  /** Plain-text digest of the card (first text section), for index snippet + aria. */
  text: string;
  /** The unified card the bubble renders via `<nova-card>`. */
  card: CardResponse | null;
  /** Responses API id of this turn, for feedback correlation. */
  responseId?: string;
  /** Token usage for this turn (summed across any tool-loop hops). */
  usage?: TokenUsage;
}

/**
 * The welcome turn's brief, kept to the least that produces a usable screen. The response schema
 * carries the rest: `kind` needs no mention because its enum already allows only `prompt` or
 * `tool`, and sections the empty state does not read are ignored, so nothing has to forbid them.
 *
 * The one thing the schema cannot express is that `label` and `value` play different parts —
 * typed only as strings, the model sets both to the same caption, and every chip then sends a
 * terse fragment instead of a question. "A short caption" was not enough to prevent that; a
 * concrete "at most five words" is, because it makes the two visibly different lengths.
 *
 * Beyond that it names no subject and gives no editorial steer, so which prompts a rep is
 * offered is the agent's own judgement.
 */
const WELCOME_BRIEF = [
  "You are composing the assistant's opening screen, shown before the rep has asked anything.",
  'Reply with a short greeting and eight starter prompts the rep can tap.',
  "A prompt's label is its button caption, at most five words; its value is the whole question",
  'to send when the rep taps it.',
].join('\n');

/** User turn that triggers the welcome card. The brief above carries the real instruction. */
const WELCOME_TRIGGER = 'Compose the welcome screen.';

/**
 * Ceiling on the starter-prompt pool. Topping up appends to it, so without a cap a rep who
 * kept tapping for more would grow the list without bound for the rest of the session.
 */
const MAX_WELCOME_ACTIONS = 24;

/**
 * Limits for the requests of a rep's chat turn. Each request fails after 90 seconds and is not
 * resent, so a stalled turn ends in the panel's error reply, whose Retry action is the only resend.
 */
const CHAT_REQUEST_OPTIONS: AiRequestOptions = { timeoutMs: 90_000, maxRetries: 0 };

/**
 * Budgets for the two halves of a position lookup. They are separate because the halves fail
 * differently: the device returns coordinates from its own cache in milliseconds, while the
 * name they reverse-geocode to is a network call (on web, a Google Maps one) that can hang.
 * Coordinates alone still let the agent reason about what is near the rep, so a slow name is
 * dropped rather than costing the position entirely. Neither is worth stalling a turn for.
 */
const COORDS_BUDGET_MS = 2000;
const PLACE_NAME_BUDGET_MS = 1500;

/** How long a resolved position stands before it is looked up again. */
const LOCATION_TTL_MS = 60_000;

/** Decimal places in the coordinate structured inputs; six places resolve to about 0.1 m. */
const COORDINATE_DECIMALS = 6;

/** Where the rep is: the coordinates of a fix and the place line the rep context states. */
interface RepPosition {
  latitude: number;
  longitude: number;
  /** "Kitchener, Ontario, Canada (43.4045, -80.2803)", or the coordinates alone when the place has no name. */
  label: string;
}

/**
 * The rep's position as the `userLatitude` and `userLongitude` structured inputs, in invariant
 * decimal degrees ("12.971600"), for the server to bind into queries. Empty without a fix, so
 * both keys are absent rather than carrying a placeholder value.
 */
function positionInputs(position: RepPosition | null): Record<string, string> {
  if (!position) return {};
  return {
    userLatitude: position.latitude.toFixed(COORDINATE_DECIMALS),
    userLongitude: position.longitude.toFixed(COORDINATE_DECIMALS),
  };
}

/** Resolve a promise, or give up on it and yield null once `ms` have passed. */
function withBudget<T>(work: Promise<T> | undefined, ms: number): Promise<T | null> {
  if (!work) return Promise.resolve(null);
  return Promise.race([
    work.catch(() => null),
    new Promise<null>(resolve => setTimeout(() => resolve(null), ms)),
  ]);
}

/**
 * Append the prompts a top-up returned, up to the cap. A prompt is a repeat if EITHER its
 * caption or its question is already on offer: asked twice, the agent readily re-proposes
 * the same idea under a reworded question ("What orders do I have?" vs "Show my orders"),
 * and matching on the question alone lets those through.
 */
function mergeActions(pool: CardAction[], incoming: CardAction[]): CardAction[] {
  const norm = (v: string) => v.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const labels = new Set(pool.map(a => norm(a.label)));
  const values = new Set(pool.map(a => norm(a.value)));
  const merged = [...pool];
  for (const action of incoming) {
    if (merged.length >= MAX_WELCOME_ACTIONS) break;
    const label = norm(action.label);
    const value = norm(action.value);
    if (labels.has(label) || values.has(value)) continue;
    labels.add(label);
    values.add(value);
    merged.push(action);
  }
  return merged;
}

/**
 * General Q&A chat over the Assistant agent.
 *
 * Rep context: Foundry rejects per-request `instructions` (and `text.format`)
 * when an agent is specified — but it DOES accept the Responses-API `input` as
 * an ARRAY of message items with explicit roles. So on the first user turn of
 * a fresh conversation we send `[ {role:'developer', content:<rep context>},
 * {role:'user', content:<message>} ]`. The developer message is stored on the
 * conversation server-side so the agent has rep context for every subsequent
 * turn; the chat thread filters role !== user/assistant out of the render.
 */
/** The origin kinds a conversation's `origin` metadata may name. */
const ORIGIN_KINDS: ReadonlySet<string> = new Set(['meeting', 'contact', 'account', 'spotlight']);

/** How long the conversation sync waits for the Ask Nova agent configuration that the same sync stores. */
const AGENT_WAIT_MS = 5000;

/** True for a conversation the history lists: an Ask Nova chat the rep has not deleted. */
function isListed(conversation: AiConversationSummary): boolean {
  return conversation.metadata.kind === 'assistant' && !conversation.deleted;
}

/**
 * A history entry for a conversation this device has not indexed, built from the metadata stamped on it when it
 * was started: `title`, `snippet`, `origin` as `kind:id`, and `createdAt`, which stands in for the last activity.
 */
function entryFromHost(conversation: AiConversationSummary): NovaConversationEntry {
  const { title, snippet, origin, createdAt } = conversation.metadata;
  const separator = (origin ?? '').indexOf(':');
  const kind = separator > 0 ? origin.slice(0, separator) : '';
  const id = separator > 0 ? origin.slice(separator + 1) : '';
  return {
    id: conversation.id,
    origin: ORIGIN_KINDS.has(kind) && id ? ({ kind, id } as ConversationOrigin) : null,
    title: title || 'New conversation',
    snippet: snippet ?? '',
    lastAt: createdAt || conversation.createdAt,
  };
}

@Injectable({ providedIn: 'root' })
export class AssistantChatFeature {
  #conversation: AiConversation | null = null;
  /** True once the rep-context developer message has been seeded on this conversation. */
  #contextSent = false;
  /** Pending attachments for the NEXT send (seeded into a context message once). */
  #pendingAttachments: AssistantAttachment[] = [];
  /** Origin of the active conversation, for index provenance. null = general. */
  #origin: ConversationOrigin | null = null;
  /** Human label of the origin entity (e.g. "Dr. Amelia Chen") for the index title. */
  #originLabel: string | null = null;
  /** Welcome card for an empty thread and the app language it was written in, cached for the app session. */
  #welcome: { language: string; card: CardResponse } | null = null;
  /** In-flight welcome fetch and its language, so two panel opens in quick succession share one turn. */
  #welcomeInFlight: { language: string; card: Promise<CardResponse | null> } | null = null;
  /** In-flight pool top-up, so nearing the end of the pool twice only fetches once. */
  #topUpInFlight: Promise<void> | null = null;
  /** Last resolved position and when it was taken, so one turn's lookup serves the next. */
  #position: { fix: RepPosition | null; at: number } | null = null;

  readonly #ai = inject(AiClientService);
  readonly #disk = inject(DiskService);
  readonly #authService = inject(AuthenticationService);
  readonly #index = inject(NovaConversationIndexService);
  readonly #geolocation = inject(GeolocationService);
  readonly #localization = inject(LocalizationService);

  isAvailable(): boolean {
    return this.#ai.isAvailable(AgentCategory.Assistant);
  }

  /**
   * Open the chat: resume the stored conversation and return its history, or an
   * empty thread when none exists. Read-only — a conversation is NOT created here;
   * that happens lazily on the first send.
   */
  async open(): Promise<AssistantChatMessage[]> {
    // General chat is origin-less. Clear any origin / pending attachments left
    // by an abandoned openFor() draft so a meeting/record context can't leak
    // into the general thread (and so a later send() indexes it as general).
    if (this.#origin || this.#pendingAttachments.length) {
      await this.endConversation();
    }
    if (this.#conversation) {
      const items = await this.#conversation.listItems();
      this.#contextSent = this.#detectContextSent(items);
      return this.#toMessages(items);
    }
    const storedId = await this.#readStoredId();
    if (storedId) {
      try {
        this.#conversation = await this.#ai.resumeConversation(AgentCategory.Assistant, storedId);
        const items = await this.#conversation.listItems();
        this.#contextSent = this.#detectContextSent(items);
        return this.#toMessages(items);
      } catch (e) {
        // Stored conversation was expired/dead on the server — drop the dead
        // wrapper AND the stored id so the next send() lazily creates a fresh
        // conversation instead of reusing the broken one.
        console.warn('Assistant resume failed; clearing stale conversation id:', e);
        this.#conversation = null;
        this.#contextSent = false;
        await this.#clearStoredId();
      }
    }
    return [];
  }

  /**
   * Find-or-draft for a "Discuss" entry point. If a conversation already exists
   * for this origin, resume it and return its history. Otherwise compose a
   * DRAFT: no Foundry conversation yet (created on first send), with the origin
   * attached so the first turn seeds context and the index records provenance.
   */
  async openFor(origin: ConversationOrigin, attachment: AssistantAttachment | null): Promise<AssistantChatMessage[]> {
    const existing = await this.#index.findByOrigin(origin);
    if (existing) {
      try {
        this.#conversation = await this.#ai.resumeConversation(AgentCategory.Assistant, existing.id);
        this.#origin = origin;
        this.#originLabel = attachment?.label ?? existing.title ?? null;
        const items = await this.#conversation.listItems();
        this.#contextSent = this.#detectContextSent(items);
        this.#pendingAttachments = [];
        return this.#toMessages(items);
      } catch (e) {
        console.warn('openFor resume failed; starting a fresh draft:', e);
        this.#conversation = null; // discard the half-resumed (broken) handle
        await this.#index.remove(existing.id);
      }
    }
    // Draft: discard any live handle, stage the attachment + origin for first send.
    await this.endConversation();
    this.#origin = origin;
    this.#originLabel = attachment?.label ?? null;
    this.#pendingAttachments = attachment ? [attachment] : [];
    return [];
  }

  /**
   * Sends `message` as the rep's next turn and resolves with the agent's reply. The first turn creates
   * the conversation. Every request of the turn, the conversation create included, takes the chat
   * limits, and `signal` aborts them all, so the turn rejects as soon as it fires.
   */
  async send(message: string, attachments?: AssistantAttachment[], signal?: AbortSignal): Promise<AssistantReply> {
    const requestOptions: AiRequestOptions = { ...CHAT_REQUEST_OPTIONS, signal };
    if (attachments?.length) {
      const merged = [...this.#pendingAttachments];
      for (const a of attachments) if (!merged.some(x => x.id === a.id && x.kind === a.kind)) merged.push(a);
      this.#pendingAttachments = merged;
    }
    if (!this.#conversation) {
      this.#conversation = await this.#ai.startConversation(AgentCategory.Assistant, {
        kind: 'assistant',
        title: (this.#originTitle() ?? message.slice(0, 60)).slice(0, 200),
        snippet: message.slice(0, 200),
        origin: this.#origin ? `${this.#origin.kind}:${this.#origin.id}` : '',
        createdAt: new Date().toISOString(),
      }, requestOptions);
      this.#contextSent = false;
      // storedId tracks ONLY the general (origin-less) conversation — it's what
      // open() resumes for the FAB chat. Origin-bearing conversations (meeting /
      // record) are tracked by the conversation index + found via openFor, so
      // they must NOT clobber the general resume pointer.
      if (!this.#origin) await this.#writeStoredId(this.#conversation.id);
    }

    // Two independent context concerns:
    //  - rep context: seeded ONCE per conversation (first turn only).
    //  - attachment context: seeded on ANY turn that carries attachments, so a
    //    mid-conversation @-attach still reaches the agent (not just turn 1).
    // When either applies we send an input-array with a developer message; a
    // plain turn with no attachments and context already sent is a bare string.
    const repContext = this.#contextSent ? null : await this.#buildRepContext();
    const attachmentContext = buildAttachmentContext(this.#pendingAttachments);
    const developerContent = [repContext, attachmentContext].filter(Boolean).join('\n\n');
    const payload: unknown = developerContent
      ? [
          { type: 'message', role: 'developer', content: developerContent },
          { type: 'message', role: 'user',      content: message },
        ]
      : message;

    // recordType = '' — the assistant has no single bound record; origin tags the surface. The
    // transport attaches the rep's session/identity context on every turn (MCP impersonation +
    // territory scoping).
    const position = await this.#currentPosition();
    this.#conversation.setStructuredInputs({
      recordType: '',
      origin: 'goapp-assistant',
      ...positionInputs(position),
    });
    const raw = await this.#conversation.send(payload, requestOptions);
    this.#contextSent = true;
    this.#pendingAttachments = [];
    const reply = this.#peelReply(raw);
    // Feedback-correlation metadata for THIS turn only — never attached inside #peelReply itself,
    // since that helper also re-peels restored history (see #toMessages) where lastTurn would be
    // stale or absent.
    const lastTurn = this.#conversation.lastTurn;
    if (lastTurn) {
      reply.responseId = lastTurn.responseId;
      reply.usage = lastTurn.usage;
    }

    // Record / refresh the index entry (provenance + preview). Fire-and-forget:
    // the local write must not delay returning the reply to the UI.
    void this.#index.upsert({
      id: this.#conversation.id,
      origin: this.#origin,
      title: this.#originTitle() ?? message.slice(0, 60),
      snippet: markdownToText(reply.text || message).slice(0, 80),
      lastAt: new Date().toISOString(),
    }).catch(e => console.error('index upsert failed:', e));

    return reply;
  }

  /** Derive a stable title from the origin, or null for general conversations. */
  #originTitle(): string | null {
    if (!this.#origin) return null;
    // Prefer the actual entity name ("Dr. Amelia Chen", "Mercy General"); fall
    // back to a generic kind label only when no label was captured.
    if (this.#originLabel) return this.#originLabel;
    const k = this.#origin.kind;
    const label = k === 'meeting' ? 'Meeting' : k.charAt(0).toUpperCase() + k.slice(1);
    return `${label} discussion`;
  }

  /**
   * Greeting + starter prompts for an empty thread, from the Assistant agent itself —
   * so the suggestions reflect the skills bound to THIS agent and this rep's context.
   *
   * Runs on a THROWAWAY conversation that is deleted immediately. It deliberately does
   * not touch `#conversation`, the stored resume pointer, or the conversation index:
   * merely opening the panel must not create a conversation the rep never used, and the
   * greeting must not survive as a ghost assistant bubble on a later resume. Tapping a
   * pill starts the real conversation through the normal send() path.
   *
   * Returns null when the agent gives back no actions — a greeting with nothing to tap
   * is worse than the static hint it would replace. Cached for the app session per app
   * language, so a language change fetches a card in the new language; callers may call
   * this on every open.
   */
  async welcome(): Promise<CardResponse | null> {
    const language = this.#localization.selectedLanguage?.code ?? '';
    if (this.#welcome?.language === language) return this.#welcome.card;
    if (this.#welcomeInFlight?.language === language) return this.#welcomeInFlight.card;
    const inFlight = {
      language,
      card: this.#fetchWelcome()
        .then(card => {
          this.#welcome = card ? { language, card } : null;
          return card;
        })
        .catch(e => {
          console.warn('Assistant welcome failed:', e);
          return null;
        })
        .finally(() => {
          if (this.#welcomeInFlight === inFlight) this.#welcomeInFlight = null;
        }),
    };
    this.#welcomeInFlight = inFlight;
    return inFlight.card;
  }

  /**
   * Quietly grow the starter-prompt pool with a fresh set from the agent, keeping the
   * greeting the rep is already looking at. Runs in the background while they browse the
   * prompts they have, so the next tap for more has new ones ready rather than waiting on
   * a turn. Silent by contract: failures are swallowed and the existing pool stands.
   *
   * Duplicates are dropped on the question text, since asking the same agent twice in one
   * session tends to surface the same obvious prompts.
   */
  /** True while a pool top-up is in flight, so the empty state can show placeholders. */
  get isToppingUpWelcome(): boolean {
    return this.#topUpInFlight !== null;
  }

  async topUpWelcomeActions(): Promise<void> {
    if (this.#topUpInFlight) return this.#topUpInFlight;
    const current = this.#welcome?.card;
    if (!current || (current.actions?.length ?? 0) >= MAX_WELCOME_ACTIONS) return;

    this.#topUpInFlight = this.#fetchWelcome((current.actions ?? []).map(a => a.label))
      .then(fresh => {
        // The rep may have started a conversation, or the card may have been replaced,
        // while this was in flight — only grow the pool this top-up was started for.
        if (!fresh?.actions?.length || this.#welcome?.card !== current) return;
        current.actions = mergeActions(current.actions ?? [], fresh.actions);
      })
      .catch(e => console.warn('Assistant welcome top-up failed:', e))
      .finally(() => (this.#topUpInFlight = null));
    return this.#topUpInFlight;
  }

  /**
   * The welcome turn's developer message. When topping up, the captions already on offer are
   * listed so the agent proposes genuinely different ideas rather than rewording its first set.
   */
  async #welcomeBrief(exclude: readonly string[]): Promise<string> {
    const parts = [await this.#buildRepContext(), WELCOME_BRIEF];
    if (exclude.length) {
      parts.push(
        `The rep has already been offered these prompts: ${exclude.join(', ')}.`
        + ' Propose eight DIFFERENT ones covering other ground. Do not reword the prompts above.',
      );
    }
    return parts.join('\n\n');
  }

  async #fetchWelcome(exclude: readonly string[] = []): Promise<CardResponse | null> {
    const origin = 'goapp-assistant-welcome';
    const conversation = await this.#ai.startConversation(AgentCategory.Assistant, {
      kind: 'assistant-welcome',
      origin,
      createdAt: new Date().toISOString(),
    });
    try {
      const position = await this.#currentPosition();
      conversation.setStructuredInputs({ recordType: '', origin, ...positionInputs(position) });
      const raw = await conversation.send([
        { type: 'message', role: 'developer', content: await this.#welcomeBrief(exclude) },
        { type: 'message', role: 'user',      content: WELCOME_TRIGGER },
      ]);
      const card = parseCardResponse(unfenceJson(raw));
      return card.actions?.length ? card : null;
    } finally {
      // One-shot: the welcome turn has no follow-up, so discard it host-side either way.
      conversation.delete().catch(() => undefined);
    }
  }

  /**
   * Start a fresh conversation: discard the current one and forget the stored id.
   * No new conversation is created here — the next message creates it (via send).
   */
  async startNew(): Promise<AssistantChatMessage[]> {
    await this.endConversation();
    await this.#clearStoredId();
    this.#origin = null;
    this.#originLabel = null;
    this.#pendingAttachments = [];
    return [];
  }

  /**
   * History sheet backing: the rep's assistant conversations from the local index, newest first, at most
   * {@link NovaConversationIndexService.MAX}, each preview as plain text. Reads only the device: this device's
   * conversations are indexed as they start, and the app sync adds those started on the rep's other devices and
   * removes those deleted there ({@link syncConversations}). Dead entries are cleaned up lazily: resume() removes
   * an entry when its conversation fails to resume.
   */
  async listConversations(): Promise<NovaConversationEntry[]> {
    return (await this.#index.list()).map(entry => ({ ...entry, snippet: markdownToText(entry.snippet) }));
  }

  /**
   * App sync step: brings the local index up to date with the conversations Foundry keeps for the rep. Reads them
   * most recently created first until {@link NovaConversationIndexService.MAX} listable ones are read, then adds
   * those not yet indexed and removes those the rep deleted. Skipped when the sync loads from the device only or
   * the rep has no Ask Nova agent; a failure leaves the index as it was.
   */
  async syncConversations(loadFromDBOnly = false): Promise<void> {
    if (loadFromDBOnly || !this.#authService.hasFeatureAction(FeatureActionsMap.NOVA_ASSISTANT)) return;
    if (!(await this.#ai.whenAvailable(AgentCategory.Assistant, AGENT_WAIT_MS))) return;
    try {
      const hosted = await this.#ai.listConversations(
        AgentCategory.Assistant,
        read => read.filter(isListed).length >= NovaConversationIndexService.MAX,
      );
      await this.#index.mergeHosted(
        hosted.filter(isListed).map(entryFromHost),
        hosted.filter(conversation => conversation.deleted).map(conversation => conversation.id),
      );
    } catch (e) {
      console.error('Ask Nova conversation sync:', e);
    }
  }

  /** Id of the conversation currently open (live handle), or null. */
  get currentConversationId(): string | null { return this.#conversation?.id ?? null; }

  /** Delete a conversation from the rep's history: Foundry marks it deleted and keeps it for audit, and the local
   *  index drops it. If it's the one currently open, the live handle + stored pointer are cleared too. */
  async deleteConversation(id: string): Promise<void> {
    await this.#ai.deleteConversation(AgentCategory.Assistant, id).catch(() => undefined);
    await this.#index.remove(id).catch(() => undefined);
    if (this.#conversation?.id === id) {
      this.#conversation = null;
      this.#contextSent = false;
      this.#origin = null;
      this.#originLabel = null;
      this.#pendingAttachments = [];
    }
    const stored = await this.#readStoredId().catch(() => null);
    if (stored === id) await this.#clearStoredId();
  }

  /** Resume a conversation chosen from the history sheet. */
  async resume(id: string, origin: ConversationOrigin | null, title?: string): Promise<AssistantChatMessage[]> {
    try {
      this.#conversation = await this.#ai.resumeConversation(AgentCategory.Assistant, id);
      this.#origin = origin;
      // Keep the existing index title (the entity name) so a later send() doesn't
      // overwrite it with a generic kind label.
      this.#originLabel = origin ? (title ?? null) : null;
      // Keep storedId pointing at the general conversation the rep is now in, so
      // a later FAB open() resumes THIS one rather than reverting to a stale id.
      // (Origin-bearing conversations are tracked by the index, not storedId.)
      if (!origin) await this.#writeStoredId(id);
      const items = await this.#conversation.listItems();
      this.#contextSent = this.#detectContextSent(items);
      this.#pendingAttachments = [];
      return this.#toMessages(items);
    } catch (e) {
      console.warn('resume failed; clearing index entry:', e);
      await this.#index.remove(id);
      return [];
    }
  }

  async endConversation(): Promise<void> {
    if (this.#conversation) {
      await this.#conversation.close().catch(() => undefined);
      this.#conversation = null;
    }
    this.#contextSent = false;
    this.#origin = null;
    this.#originLabel = null;
    this.#pendingAttachments = [];
  }

  /**
   * Where the rep is - the coordinates of a fix and its place line, "Kitchener, Ontario, Canada
   * (43.4045, -80.2803)" - or null when that cannot be had promptly. Reads the same
   * `GeolocationService` the agent's `get_current_location` tool reads, so the prompt, the
   * structured inputs and the tool never disagree.
   *
   * Memoized for {@link LOCATION_TTL_MS} so the welcome turn and the rep's messages do not each
   * pay for a fix. A refusal is worth remembering; a timeout is not.
   */
  async #currentPosition(): Promise<RepPosition | null> {
    if (this.#position && Date.now() - this.#position.at < LOCATION_TTL_MS) return this.#position.fix;

    // Permission first, and never init() - the service raises a permission prompt from there,
    // and a turn starting is not the moment to ask for location.
    const enabled = await firstValueFrom(this.#geolocation.isEnabled$ ?? of(false)).catch(() => false);
    if (!enabled) {
      this.#position = { fix: null, at: Date.now() };
      return null;
    }

    const coords = await withBudget(this.#geolocation.getCurrentPosition(), COORDS_BUDGET_MS);
    // A timed-out or failed fix is not an answer - leave the memo alone so the next turn tries
    // again rather than standing on a miss for the next minute.
    if (typeof coords?.latitude !== 'number' || typeof coords?.longitude !== 'number') return null;

    const point = `${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`;
    const place = await withBudget(this.#geolocation.getCurrentPositionName(), PLACE_NAME_BUDGET_MS);
    const named = place
      ? [place.locality, place.administrativeArea, place.countryName].filter(Boolean).join(', ')
      : '';

    const fix: RepPosition = {
      latitude: coords.latitude,
      longitude: coords.longitude,
      label: named ? `${named} (${point})` : point,
    };
    this.#position = { fix, at: Date.now() };
    return fix;
  }

  /**
   * Markdown rep-profile block lifted into a developer message on the first
   * turn. Carries the core rep-profile field set plus username + user id (so
   * the agent can answer "what's my user id").
   */
  async #buildRepContext(): Promise<string> {
    const user: any = this.#authService.user ?? {};
    const now = new Date();
    // The rep's zone, resolved the same way `userTimeZone` is, so the stamp and the structured input
    // agree. Both the date and the clock are given: a meeting later today is otherwise unplaceable.
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateLabel = now.toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone,
    });
    const timeLabel = now.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone,
    });

    const place = (await this.#currentPosition())?.label;

    const lines: string[] = [
      `Today is ${dateLabel}, and the time is ${timeLabel} (${timeZone}).`,
      // Omitted entirely when there is no fix: a line saying the location is unknown only
      // invites the agent to remark on it.
      ...(place ? [`They are currently at ${place}.`] : []),
      '',
      '## Current rep (the user you are talking to)',
    ];

    const fullName = [user.academicTitle, user.displayName].filter(Boolean).join(' ').trim();
    if (fullName)               lines.push(`- Name: ${fullName}`);
    if (user.userPrincipalName) lines.push(`- Username: ${user.userPrincipalName}`);
    if (user.mail)              lines.push(`- Email: ${user.mail}`);
    if (user.systemUserID)      lines.push(`- User ID: ${user.systemUserID}`);
    if (user.jobTitle)          lines.push(`- Role: ${user.jobTitle}`);
    if (user.businessUnitName)  lines.push(`- Business Unit: ${user.businessUnitName}`);
    if (user.positionName)      lines.push(`- Primary position: ${user.positionName}`);

    const territories = Array.isArray(user.positions)
      ? user.positions.map((p: any) => p?.name).filter(Boolean).join(', ')
      : '';
    if (territories) lines.push(`- Territories / Positions: ${territories}`);

    return lines.join('\n');
  }

  /**
   * Peel an assistant turn into a renderable reply. Structured turns are
   * `card_response` envelopes ({title, sections[], actions}); plain-prose turns
   * (an agent whose structured_outputs is off, or a one-off non-compliant reply)
   * fall back to a single text-section card. {@link parseCardResponse} never throws
   * and drops only malformed sections, so the live send() and the history restore
   * render identically and neither ever shows an empty bubble.
   */
  #peelReply(raw: string): AssistantReply {
    if (!raw) return { text: '', card: null };
    const card = parseCardResponse(unfenceJson(raw));
    return { text: this.#cardDigest(card), card };
  }

  /** First text-section prose (markdown stripped of nothing — used only as a snippet/aria digest). */
  #cardDigest(card: CardResponse): string {
    const text = card.sections.find(s => s.type === 'text');
    if (text && text.type === 'text') return text.text;
    const callout = card.sections.find(s => s.type === 'callout');
    if (callout && callout.type === 'callout') return callout.text;
    return card.title ?? '';
  }

  /**
   * Conversation history → renderable thread. Drops anything that isn't a
   * user or assistant turn (developer/system context items live in history
   * but never render). Assistant turns are JSON envelopes (structured_outputs);
   * peel them so the bubble shows text + decorations.
   *
   * RESTORE PATH for attachments: when a conversation had attachments, they were
   * embedded as a developer context message (via buildAttachmentContext). We parse
   * that block here to recover the attachment labels and attach them to the FIRST
   * user message in the restored thread, giving best-effort display. The parse is
   * fragile-by-design (label extraction only) — no ids are needed for rendering.
   */
  #toMessages(items: AiConversationItem[]): AssistantChatMessage[] {
    // Collect attachment labels from the developer context block (if present).
    // Format produced by buildAttachmentContext: "- Kind «label» (…_id=…). …"
    const restoredAttachments = this.#parseAttachmentsFromContext(items);

    const out: AssistantChatMessage[] = [];
    let firstUserMessageSeen = false;
    for (const item of items) {
      if (!item.text) continue;
      if (item.role === 'user') {
        const msg: AssistantChatMessage = { role: 'user', text: item.text };
        // Attach recovered labels only to the first user turn — that's the turn
        // that had the attachments (attachment context is seeded once per send).
        if (!firstUserMessageSeen && restoredAttachments.length) {
          msg.attachments = restoredAttachments;
        }
        firstUserMessageSeen = true;
        out.push(msg);
        continue;
      }
      if (item.role !== 'assistant') {
        // developer / system context — kept on the conversation server-side
        // for the agent, but not rendered in the chat thread.
        continue;
      }
      // assistant turn — peel the structured envelope (or fall back to prose).
      out.push({ role: 'assistant', ...this.#peelReply(item.text) });
    }
    return out;
  }

  /**
   * Parse the attachment labels embedded in the developer context block by
   * buildAttachmentContext(). Returns best-effort AssistantAttachment stubs
   * (label + kind only — ids are not needed for rendering pill chips).
   *
   * Line format: "- Kind «label» (…_id=…). …"
   */
  #parseAttachmentsFromContext(items: AiConversationItem[]): AssistantAttachment[] {
    const devItem = items.find(
      i => (i.role === 'developer' || i.role === 'system') && !!i.text,
    );
    if (!devItem?.text) return [];

    const result: AssistantAttachment[] = [];
    // Match lines like: "- Contact «Dr. Smith» (contact_id=…)"
    const lineRe = /^- (Contact|Account|Meeting|Meeting transcript) «([^»]+)»/im;
    for (const line of devItem.text.split('\n')) {
      const m = line.match(lineRe);
      if (!m) continue;
      const [, kindRaw, label] = m;
      const kind = kindRaw.toLowerCase().replace('meeting transcript', 'transcript') as AssistantAttachment['kind'];
      // id is not needed for display — use empty string as a safe sentinel.
      result.push({ kind, id: '', label } as AssistantAttachment);
    }
    return result;
  }

  /** True if a developer/system context item exists in the conversation history. */
  #detectContextSent(items: AiConversationItem[]): boolean {
    return items.some(item => (item.role === 'developer' || item.role === 'system') && !!item.text);
  }

  async #readStoredId(): Promise<string | null> {
    const doc = await this.#disk.retrieve(DB_KEY_PREFIXES.ASSISTANT_CONVERSATION, true);
    return doc?.raw?.id ?? null;
  }

  async #writeStoredId(id: string): Promise<void> {
    await this.#disk.updateOrInsert(
      DB_KEY_PREFIXES.ASSISTANT_CONVERSATION,
      () => ({ raw: { id } }),
    ).catch(e => console.error('Persist assistant conversation id failed:', e));
  }

  async #clearStoredId(): Promise<void> {
    await this.#disk.updateOrInsert(
      DB_KEY_PREFIXES.ASSISTANT_CONVERSATION,
      () => ({ raw: { id: null } }),
    ).catch(e => console.error('Clear assistant conversation id failed:', e));
  }
}
